import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsSuperAdmin } from "./useIsSuperAdmin";

export interface UserCompanyRole {
  role: "owner" | "admin" | "manager" | "recruiter" | "member";
  scope_type: string | null;
  scope_id: string | null;
}

export interface UserCompany {
  id: string; // membership id
  company: {
    id: string;
    name: string;
    industry: string | null;
    logo_url: string | null;
    verified: boolean;
    domain: string | null;
    website: string | null;
    description: string | null;
    location: string | null;
    social_links: { careers_url?: string } | null;
    company_type?: 'business' | 'organization' | 'subsidiary';
    billing_mode?: 'independent' | 'inherit_parent';
    parent_company_id?: string;
    stripe_customer_id?: string;
  };
  roles: UserCompanyRole[];
  verified: boolean;
  joined_at: string;
  location: {
    id: string;
    name: string;
  } | null;
  department: {
    id: string;
    name: string;
  } | null;
  name?: string; // For compatibility with settings page
  company_type?: 'business' | 'organization' | 'subsidiary';
  billing_mode?: 'independent' | 'inherit_parent';
  stripe_customer_id?: string;
}

export function useUserCompanies(userId: string | null, includeUnverified: boolean = false) {
  // Check if user is super admin
  const { data: isSuperAdmin, isLoading: checkingAdmin } = useIsSuperAdmin();

  return useQuery({
    queryKey: ["user-companies", userId, isSuperAdmin, includeUnverified],
    queryFn: async () => {
      if (!userId) return { verified: [], pending: [] };

      // SUPER ADMIN PATH: Fetch ALL companies
      if (isSuperAdmin) {
        // Fetch all companies in the system
        const { data: allCompanies, error: companiesError } = await supabase
          .from("companies")
          .select(`
            id,
            name,
            industry,
            logo_url,
            verified,
            domain,
            website,
            description,
            location,
            social_links
          `)
          .order("name");

        if (companiesError) throw companiesError;
        if (!allCompanies || allCompanies.length === 0) return { verified: [], pending: [] };

        // Check which companies the super admin is actually a member of
        const { data: memberships } = await supabase
          .from("company_members")
          .select("company_id, id")
          .eq("user_id", userId)
          .eq("verified", true);

        const membershipMap = new Map(
          memberships?.map(m => [m.company_id, m.id]) || []
        );

        // Fetch roles for actual memberships
        const membershipIds = Array.from(membershipMap.values());
        const { data: roles } = await supabase
          .from("company_member_roles")
          .select("member_id, role, scope_type, scope_id")
          .in("member_id", membershipIds);

        const rolesMap = new Map<string, UserCompanyRole[]>();
        roles?.forEach((role) => {
          if (!rolesMap.has(role.member_id)) {
            rolesMap.set(role.member_id, []);
          }
          rolesMap.get(role.member_id)!.push({
            role: role.role as UserCompanyRole["role"],
            scope_type: role.scope_type,
            scope_id: role.scope_id,
          });
        });

        // Transform all companies to UserCompany format
        const transformed = allCompanies.map((company: any) => {
          const membershipId = membershipMap.get(company.id);
          return {
            id: membershipId || `super-admin-${company.id}`, // Synthetic ID for non-member companies
            company: {
              id: company.id,
              name: company.name,
              industry: company.industry,
              logo_url: company.logo_url,
              verified: company.verified,
              domain: company.domain,
              website: company.website,
              description: company.description,
              location: company.location,
              social_links: company.social_links as { careers_url?: string } | null,
              company_type: company.company_type,
              billing_mode: company.billing_mode,
              parent_company_id: company.parent_company_id,
              stripe_customer_id: company.stripe_customer_id,
            },
            name: company.name,
            company_type: company.company_type,
            billing_mode: company.billing_mode,
            stripe_customer_id: company.stripe_customer_id,
            roles: membershipId ? (rolesMap.get(membershipId) || []) : [{ role: "admin" as const, scope_type: "company", scope_id: null }], // Super admins get implicit admin role
            verified: true, // Always true for super admins
            joined_at: new Date().toISOString(),
            location: null,
            department: null,
          };
        });

        return { verified: transformed, pending: [] };
      }

      // REGULAR USER PATH: Fetch only memberships

      // Fetch verified memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from("company_members")
        .select(`
          id,
          company_id,
          verified,
          joined_at,
          location_id,
          department_id,
          companies!inner (
            id,
            name,
            industry,
            logo_url,
            verified,
            domain,
            website,
            description,
            location,
            social_links
          ),
          company_locations (
            id,
            name
          ),
          company_departments (
            id,
            name
          )
        `)
        .eq("user_id", userId)
        .eq("verified", true)
        .order("joined_at", { ascending: false });

      // Fetch pending memberships if requested
      const pendingQuery = includeUnverified ? supabase
        .from("company_members")
        .select(`
          id,
          company_id,
          verified,
          joined_at,
          location_id,
          department_id,
          companies!inner (
            id,
            name,
            industry,
            logo_url,
            verified,
            domain,
            website,
            description,
            location,
            social_links
          ),
          company_locations (
            id,
            name
          ),
          company_departments (
            id,
            name
          )
        `)
        .eq("user_id", userId)
        .eq("verified", false)
        .order("joined_at", { ascending: false }) : null;

      const pendingResult = pendingQuery ? await pendingQuery : { data: null, error: null };
      const { data: pendingMemberships, error: pendingError } = pendingResult;

      if (membershipsError) throw membershipsError;
      if (pendingError) console.error("Error fetching pending memberships:", pendingError);
      
      if (!memberships || memberships.length === 0) {
        const pending = pendingMemberships ? transformMemberships(pendingMemberships) : [];
        return { verified: [], pending };
      }

      // Fetch all roles for verified memberships
      const membershipIds = memberships.map((m) => m.id);
      const { data: roles, error: rolesError } = await supabase
        .from("company_member_roles")
        .select("member_id, role, scope_type, scope_id")
        .in("member_id", membershipIds);

      if (rolesError) throw rolesError;

      // Map roles to memberships
      const rolesMap = new Map<string, UserCompanyRole[]>();
      roles?.forEach((role) => {
        if (!rolesMap.has(role.member_id)) {
          rolesMap.set(role.member_id, []);
        }
        rolesMap.get(role.member_id)!.push({
          role: role.role as UserCompanyRole["role"],
          scope_type: role.scope_type,
          scope_id: role.scope_id,
        });
      });

      // Transform to UserCompany format
      const verified = transformMembershipsWithRoles(memberships, rolesMap);
      const pending = pendingMemberships ? transformMemberships(pendingMemberships) : [];

      return { verified, pending };
    },
    enabled: !!userId && !checkingAdmin, // Wait for admin check to complete
    staleTime: 10 * 60 * 1000,      // 10 minutes - companies rarely change
    gcTime: 15 * 60 * 1000,         // 15 minutes retention
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

function transformMembershipsWithRoles(memberships: any[], rolesMap: Map<string, UserCompanyRole[]>): UserCompany[] {
  return memberships.map((membership) => ({
    id: membership.id,
    company: {
      id: membership.companies.id,
      name: membership.companies.name,
      industry: membership.companies.industry,
      logo_url: membership.companies.logo_url,
      verified: membership.companies.verified,
      domain: membership.companies.domain,
      website: membership.companies.website,
      description: membership.companies.description,
      location: membership.companies.location,
      social_links: membership.companies.social_links as { careers_url?: string } | null,
    },
    roles: rolesMap.get(membership.id) || [],
    verified: membership.verified,
    joined_at: membership.joined_at,
    location: membership.company_locations,
    department: membership.company_departments,
  }));
}

function transformMemberships(memberships: any[]): UserCompany[] {
  return memberships.map((membership) => ({
    id: membership.id,
    company: {
      id: membership.companies.id,
      name: membership.companies.name,
      industry: membership.companies.industry,
      logo_url: membership.companies.logo_url,
      verified: membership.companies.verified,
      domain: membership.companies.domain,
      website: membership.companies.website,
      description: membership.companies.description,
      location: membership.companies.location,
      social_links: membership.companies.social_links as { careers_url?: string } | null,
    },
    roles: [],
    verified: membership.verified,
    joined_at: membership.joined_at,
    location: membership.company_locations,
    department: membership.company_departments,
  }));
}
